import React, { useState, useEffect, useRef } from "react";
import {
  Settings,
  Play,
  CheckCircle2,
  XCircle,
  Coffee,
  Plus,
  Trash2,
  Edit3,
  Heart,
  Star,
  Sparkles,
  Wand2,
  MessageCircle,
} from "lucide-react";

const DEFAULT_TASKS = [
  "完成一张卡比迷宫闯关",
  "认真写3行字帖",
  "全家大声读一首古诗",
  "去院子观察三角梅并画下来",
  "看5分钟英文原版动画",
  "给柠檬树和无花果浇水",
  "自己整理明天要用的书包",
  "跳绳100个",
  "当小会计算算今天买菜的钱",
];

// ！！！你的智谱 API Key ！！！
const API_KEY = "96658051bf954c1ca80d9686d6c5b39d.JqLCJcCmK9kq5GGq";

const COLORS = [
  "#FFB3BA",
  "#FFDFBA",
  "#FFFFBA",
  "#BAFFC9",
  "#BAE1FF",
  "#E8BAFF",
  "#FFBAE8",
  "#FFD8B1",
  "#C1F0F6",
];

export default function App() {
  const [tasks, setTasks] = useState(DEFAULT_TASKS);
  const [isSpinning, setIsSpinning] = useState(false);
  const [todayTasks, setTodayTasks] = useState<any[]>([]);

  // 三个指针的独立角度
  const [rotations, setRotations] = useState([0, 120, 240]);

  const [stats, setStats] = useState({
    done: 0,
    failed: 0,
    leave: 0,
    leaveCards: 3,
  });

  const [showSettings, setShowSettings] = useState(false);
  const [newTaskInput, setNewTaskInput] = useState("");

  // AI 相关状态
  const [aiPraise, setAiPraise] = useState("");
  const [isGeneratingPraise, setIsGeneratingPraise] = useState(false);
  const [aiInspirationTopic, setAiInspirationTopic] = useState("");
  const [isGeneratingTasks, setIsGeneratingTasks] = useState(false);

  // === 聊天对话框相关状态 ===
  const [showChat, setShowChat] = useState(false);
  const [voiceRatio, setVoiceRatio] = useState(50); // 默认50%几率回复语音
  const [messages, setMessages] = useState<any[]>([
    {
      role: "ai",
      text: "呼噜呼噜... 安安宝贝你好呀！我是你的好朋友-哈基米。今天想和我聊点什么喵~ ฅ^•ﻌ•^ฅ",
      type: "text",
    },
  ]);
  const [inputText, setInputText] = useState("");
  const [isChatting, setIsChatting] = useState(false);

  // 语音播放控制
  const [playingMsgIndex, setPlayingMsgIndex] = useState<any>(null);
  const audioRef = useRef<any>(null);
  const chatContainerRef = useRef<any>(null);

  // 每次有新消息自动滚动到底部
  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop =
        chatContainerRef.current.scrollHeight;
    }
  }, [messages, isChatting]);

  // 初始化转盘
  useEffect(() => {
    setRotations([
      Math.floor(Math.random() * 360),
      Math.floor(Math.random() * 360),
      Math.floor(Math.random() * 360),
    ]);
  }, []);

  // 通用文本生成接口 (夸夸精灵、生成任务使用)
  const callGemini = async (prompt: any) => {
    if (!API_KEY) {
      alert("请先在代码中填入您的 API Key 哦！");
      return null;
    }
    try {
      const response = await fetch(
        "https://open.bigmodel.cn/api/paas/v4/chat/completions",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${API_KEY}`,
          },
          body: JSON.stringify({
            model: "glm-4.5-air", // 使用你有1200万额度的模型
            messages: [{ role: "user", content: prompt }],
          }),
        }
      );
      const data = await response.json();
      if (data.error) {
        alert("智谱官方报错啦：" + data.error.message);
        return null;
      }
      return data.choices[0].message.content;
    } catch (error: any) {
      console.error("AI 请求失败:", error);
      alert("网络连接失败！具体原因：" + error.message);
      return null;
    }
  };

  // === 智谱情感语音合成 (TTS) 接口 ===
  const fetchTTS = async (text: any) => {
    try {
      const response = await fetch(
        "https://open.bigmodel.cn/api/paas/v4/audio/speech",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${API_KEY}`,
          },
          body: JSON.stringify({
            model: "glm-tts",
            input: text,
            voice: "jam", // 童声/女声
            response_format: "wav", // 强制要求 wav 格式，解决无声问题
            speed: 1.2,
          }),
        }
      );

      const contentType = response.headers.get("content-type");
      if (contentType && contentType.includes("application/json")) {
        const errJson = await response.json();
        console.error("TTS返回了错误信息:", errJson);
        return null;
      }

      if (!response.ok) throw new Error("TTS Request failed");

      const blob = await response.blob();
      return URL.createObjectURL(new Blob([blob], { type: "audio/wav" }));
    } catch (e: any) {
      console.error("语音合成失败", e);
      return null;
    }
  };

  // 播放/停止 语音消息
  const playAudio = (url: any, index: any) => {
    if (audioRef.current) {
      audioRef.current.pause();
    }
    if (playingMsgIndex === index) {
      setPlayingMsgIndex(null);
      return;
    }
    const audio = new Audio(url);
    audioRef.current = audio;
    setPlayingMsgIndex(index);
    audio.play();
    audio.onended = () => {
      setPlayingMsgIndex(null);
    };
  };

  // === 发送聊天消息并智能分发 (强制 JSON 解析) ===
  const handleSendMessage = async () => {
    if (!inputText.trim() || isChatting) return;

    const userMsg = inputText.trim();
    const newMessages = [
      ...messages,
      { role: "user", text: userMsg, type: "text" },
    ];
    setMessages(newMessages);
    setInputText("");
    setIsChatting(true);

    const systemPrompt = `你现在是一只超级可爱、会说话的魔法小猫咪（哈基米），是6岁小女孩安安的专属陪聊宠物。你平时最喜欢在院子里的三角梅下抓蝴蝶，或者在柠檬树上睡午觉，你也和她一样喜欢星之卡比。
    请用极其软萌、有点傲娇又很粘人的小猫语气和她进行微信式聊天，你一般自称“本哈基米”。千万不要说教，每次回复控制在30~50字。要自然加入猫咪拟声词（喵呜~、喵喵！、呼噜呼噜...），多用"~""！"等标点让语气生动。

    【输出格式——必须严格遵守】
    你只能输出一个 JSON 对象，不要输出任何解释或代码块标记：
    {"emotion":"情感词","text":"说话内容","emoji":"颜文字"}

    - emotion：从【开心、撒娇、温柔、俏皮、激动、委屈】中只选一个。
    - text：要念安安听的话，30~50字；里面绝对不要出现方括号、Emoji、颜文字、星号或任何特殊符号（语音引擎会念出来），禁止出现 "~"、"！"）。
    - emoji：1~2个猫咪颜文字或Emoji（如 ฅ^•ﻌ•^ฅ🐾），只用于气泡显示，不会被念出来。`;

    const chatHistory = newMessages.map((m: any) => ({
      role: m.role === "ai" ? "assistant" : "user",
      content: m.text,
    }));

    try {
      const response = await fetch(
        "https://open.bigmodel.cn/api/paas/v4/chat/completions",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${API_KEY}`,
          },
          body: JSON.stringify({
            model: "glm-4.5-air",
            messages: [
              { role: "system", content: systemPrompt },
              ...chatHistory,
            ],
            response_format: { type: "json_object" }, // 强制大模型输出 JSON
          }),
        }
      );
      const data = await response.json();

      if (data.error) {
        setMessages((prev: any) => [
          ...prev,
          { role: "ai", text: "哎呀，魔法信号断了，精灵没听清~", type: "text" },
        ]);
      } else if (data.choices && data.choices[0]) {
        const rawReply = data.choices[0].message.content;

        let aiReplyObj = {
          emotion: "温柔",
          text: rawReply,
          emoji: "🐾",
        };

        // 解析大模型返回的 JSON
        try {
          const cleaned = rawReply
            .replace(/```json/g, "")
            .replace(/```/g, "")
            .trim();
          const match = cleaned.match(/\{[\s\S]*\}/);
          if (match) {
            aiReplyObj = JSON.parse(match[0]);
          }
        } catch (e) {
          console.error("AI未按格式输出JSON:", rawReply);
          aiReplyObj.text = rawReply.replace(
            /[^\u4e00-\u9fa5A-Za-z0-9，。！？、~…—：；\s]/g,
            ""
          );
        }

        // 界面显示的文字组合
        const displayMessage = `[${aiReplyObj.emotion}] ${aiReplyObj.text} ${aiReplyObj.emoji}`;
        const shouldUseVoice = Math.random() * 100 < voiceRatio;

        if (shouldUseVoice) {
          setMessages((prev: any) => [
            ...prev,
            { role: "ai", text: displayMessage, type: "loading_audio" },
          ]);

          // 只把纯净的 text 发给 TTS 引擎
          const audioUrl = await fetchTTS(aiReplyObj.text);

          setMessages((prev: any) => {
            const newMsgs = [...prev];
            if (audioUrl) {
              newMsgs[newMsgs.length - 1] = {
                role: "ai",
                text: displayMessage,
                type: "audio",
                audioUrl: audioUrl,
              };
            } else {
              // 音频生成失败，降级为文字
              newMsgs[newMsgs.length - 1] = {
                role: "ai",
                text: displayMessage,
                type: "text",
              };
            }
            return newMsgs;
          });
        } else {
          setMessages((prev: any) => [
            ...prev,
            { role: "ai", text: displayMessage, type: "text" },
          ]);
        }
      }
    } catch (e: any) {
      setMessages((prev: any) => [
        ...prev,
        {
          role: "ai",
          text: "网络好像打盹了，稍后再跟哈基米说话哦~",
          type: "text",
        },
      ]);
    }
    setIsChatting(false);
  };

  const generatePraise = async () => {
    setIsGeneratingPraise(true);
    const finishedTasks = todayTasks
      .filter((t) => t.status === "done")
      .map((t) => t.text)
      .join("、");
    const onLeaveTasks = todayTasks.filter((t) => t.status === "leave").length;

    let prompt = `你现在是一个非常温柔、充满童趣的AI夸夸精灵。你正在鼓励一个6岁的小女孩，她叫安安。
    请用不超过50个字，带上可爱的Emoji，给她一段专属鼓励。
    语气要像童话里的精灵，不要像老师说教。`;

    if (finishedTasks) {
      prompt += `\n她今天完成了这些挑战：${finishedTasks}。请夸奖她的努力。`;
    }
    if (onLeaveTasks > 0) {
      prompt += `\n她今天还使用了请假卡休息。请温柔地告诉她：合理休息也是一种很棒的能力，懂得照顾自己很棒。`;
    }
    if (!finishedTasks && onLeaveTasks === 0) {
      prompt += `\n她今天遇到了点困难，没有完成任务。请温柔地抱抱她，告诉她没关系，明天会有新魔法。`;
    }

    const result = await callGemini(prompt);
    if (result) setAiPraise(result);
    setIsGeneratingPraise(false);
  };

  const generateTaskInspiration = async () => {
    if (!aiInspirationTopic.trim()) return;
    setIsGeneratingTasks(true);
    const prompt = `我正在为一个6岁的女孩(安安)设计日常微小挑战任务。要求：极低门槛、有趣、游戏化。
    请根据这个主题：“${aiInspirationTopic}”，生成3个任务。
    每个任务不超过15个字。
    请严格只返回JSON数组格式，像这样：["任务1", "任务2", "任务3"]，不要有任何其他解释文字和Markdown标记。`;

    const result = await callGemini(prompt);
    if (result) {
      try {
        const cleaned = result
          .replace(/```json/g, "")
          .replace(/```/g, "")
          .trim();
        const newTasks = JSON.parse(cleaned);
        if (Array.isArray(newTasks)) {
          setTasks([...tasks, ...newTasks]);
          setAiInspirationTopic("");
          alert("✨ 灵感任务已成功加入扭蛋机！");
        }
      } catch (e: any) {
        alert("AI 生成的格式有些调皮，请重试一下。");
      }
    }
    setIsGeneratingTasks(false);
  };

  const spinWheel = () => {
    if (isSpinning || tasks.length === 0) return;
    setIsSpinning(true);
    setTodayTasks([]);
    setAiPraise("");

    const baseSpins = [360 * 5, 360 * 6, 360 * 7];
    const newRotations = rotations.map(
      (rot, i) => rot + baseSpins[i] + Math.floor(Math.random() * 360)
    );

    setRotations(newRotations);

    setTimeout(() => {
      calculateSelectedTasks(newRotations);
      setIsSpinning(false);
    }, 4000);
  };

  const calculateSelectedTasks = (currentRotations: any) => {
    const numTasks = tasks.length;
    const anglePerTask = 360 / numTasks;

    const selectedIndices = currentRotations.map((rot: any) => {
      let effectiveAngle = (rot % 360) + 90;
      let selectedAngle = (360 - effectiveAngle) % 360;
      if (selectedAngle < 0) selectedAngle += 360;
      return Math.floor(selectedAngle / anglePerTask) % numTasks;
    });

    const uniqueIndices = Array.from(new Set(selectedIndices));
    const newTodayTasks = uniqueIndices.map((index: any) => ({
      id: Date.now() + index,
      text: tasks[index],
      status: "pending",
    }));
    setTodayTasks(newTodayTasks);
  };

  const handleTaskAction = (taskId: any, action: any) => {
    setTodayTasks((prev) =>
      prev.map((t) => {
        if (t.id === taskId) {
          if (t.status !== "pending") return t;
          if (action === "done") setStats((s) => ({ ...s, done: s.done + 1 }));
          else if (action === "failed")
            setStats((s) => ({ ...s, failed: s.failed + 1 }));
          else if (action === "leave") {
            if (stats.leaveCards <= 0) {
              alert("本月的请假卡已经用完啦！必须勇敢面对挑战哦！");
              return t;
            }
            setStats((s) => ({
              ...s,
              leave: s.leave + 1,
              leaveCards: s.leaveCards - 1,
            }));
          }
          return { ...t, status: action };
        }
        return t;
      })
    );
  };

  const renderWheel = () => {
    const numTasks = Math.max(tasks.length, 1);
    const anglePerTask = 360 / numTasks;
    let currentAngle = 0;

    const paths = tasks.map((task: any, index: any) => {
      const startAngle = currentAngle;
      const endAngle = currentAngle + anglePerTask;
      currentAngle = endAngle;

      const x1 = 50 + 50 * Math.cos((Math.PI * startAngle) / 180);
      const y1 = 50 + 50 * Math.sin((Math.PI * startAngle) / 180);
      const x2 = 50 + 50 * Math.cos((Math.PI * endAngle) / 180);
      const y2 = 50 + 50 * Math.sin((Math.PI * endAngle) / 180);

      const largeArcFlag = anglePerTask > 180 ? 1 : 0;
      const pathData = `M 50 50 L ${x1} ${y1} A 50 50 0 ${largeArcFlag} 1 ${x2} ${y2} Z`;

      const textAngle = startAngle + anglePerTask / 2;
      const textRadius = 35;
      const textX = 50 + textRadius * Math.cos((Math.PI * textAngle) / 180);
      const textY = 50 + textRadius * Math.sin((Math.PI * textAngle) / 180);

      return (
        <g key={index}>
          <path
            d={pathData}
            fill={COLORS[index % COLORS.length]}
            stroke="white"
            strokeWidth="0.5"
          />
          <text
            x={textX}
            y={textY}
            fill="#4b5563"
            fontSize="3.5"
            fontWeight="bold"
            textAnchor="middle"
            transform={`rotate(${textAngle + 90} ${textX} ${textY})`}
            className="pointer-events-none select-none"
          >
            {task.length > 8 ? task.substring(0, 8) + "..." : task}
          </text>
        </g>
      );
    });

    return (
      <div className="relative w-full max-w-md aspect-square mx-auto">
        <div className="absolute inset-0 rounded-full border-4 border-stone-700 shadow-xl overflow-hidden bg-white">
          <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
            {paths}
          </svg>
        </div>

        <div className="absolute inset-0 pointer-events-none drop-shadow-lg">
          <div
            className="absolute inset-0 origin-center"
            style={{
              transform: `rotate(${rotations[0]}deg)`,
              transition: "transform 3s cubic-bezier(0.2, 0.8, 0.3, 1)",
            }}
          >
            <svg viewBox="0 0 100 100" className="w-full h-full">
              <path d="M 48 50 L 50 15 L 52 50 Z" fill="#EF4444" />
            </svg>
          </div>

          <div
            className="absolute inset-0 origin-center"
            style={{
              transform: `rotate(${rotations[1]}deg)`,
              transition: "transform 3.5s cubic-bezier(0.2, 0.8, 0.25, 1)",
            }}
          >
            <svg viewBox="0 0 100 100" className="w-full h-full">
              <path d="M 48.5 50 L 50 15 L 51.5 50 Z" fill="#3B82F6" />
            </svg>
          </div>

          <div
            className="absolute inset-0 origin-center"
            style={{
              transform: `rotate(${rotations[2]}deg)`,
              transition: "transform 4s cubic-bezier(0.2, 0.8, 0.2, 1)",
            }}
          >
            <svg viewBox="0 0 100 100" className="w-full h-full">
              <path d="M 49 50 L 50 15 L 51 50 Z" fill="#10B981" />
            </svg>
          </div>

          <svg viewBox="0 0 100 100" className="absolute inset-0 w-full h-full">
            <circle cx="50" cy="50" r="5" fill="#374151" />
            <circle cx="50" cy="50" r="2" fill="white" />
          </svg>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-stone-50 text-stone-800 font-sans p-4 md:p-8 flex flex-col relative pb-20 md:pb-8">
      <header className="flex justify-between items-center mb-6 bg-white p-4 rounded-2xl shadow-sm border border-stone-100">
        <div className="flex items-center gap-3">
          <Sparkles className="w-8 h-8 text-yellow-500" />
          <h1 className="text-xl md:text-3xl font-black text-stone-700">
            安安的超级通关扭蛋机
          </h1>
        </div>
        <button
          onClick={() => setShowSettings(true)}
          className="p-2 rounded-full hover:bg-stone-100 text-stone-500 transition"
        >
          <Settings className="w-6 h-6" />
        </button>
      </header>

      <div className="flex flex-col lg:flex-row gap-6 flex-1">
        <div className="lg:w-1/2 flex flex-col items-center justify-center bg-white rounded-3xl p-6 md:p-8 shadow-sm border border-stone-100">
          {renderWheel()}
          <button
            onClick={spinWheel}
            disabled={isSpinning || tasks.length === 0}
            className={`mt-10 px-8 py-4 rounded-full text-xl font-bold text-white shadow-lg transition-all flex items-center gap-2
              ${
                isSpinning
                  ? "bg-stone-300 cursor-not-allowed scale-95"
                  : "bg-gradient-to-r from-orange-400 to-pink-500 hover:scale-105 active:scale-95"
              }`}
          >
            <Play className="w-6 h-6 fill-current" />
            {isSpinning ? "命运指针转动中..." : "启动！抽取3个挑战！"}
          </button>
        </div>

        <div className="lg:w-1/2 flex flex-col gap-6">
          <div className="bg-white rounded-3xl p-5 md:p-6 shadow-sm border border-stone-100 flex-1">
            <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
              <Star className="w-6 h-6 text-yellow-400 fill-current" />{" "}
              今日掉落的挑战
            </h2>

            {todayTasks.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-40 text-stone-400 text-center">
                <p>今天还没有抽卡哦！</p>
              </div>
            ) : (
              <div className="flex flex-col gap-3">
                {todayTasks.map((task: any) => (
                  <div
                    key={task.id}
                    className="flex flex-col xl:flex-row justify-between items-start xl:items-center p-3 md:p-4 bg-stone-50 rounded-2xl border border-stone-200 gap-3"
                  >
                    <span
                      className={`text-base md:text-lg font-medium ${
                        task.status !== "pending"
                          ? "line-through text-stone-400"
                          : ""
                      }`}
                    >
                      {task.text}
                    </span>

                    {task.status === "pending" ? (
                      <div className="flex flex-wrap gap-2 w-full xl:w-auto justify-end">
                        <button
                          onClick={() => handleTaskAction(task.id, "done")}
                          className="flex items-center gap-1 px-3 py-2 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition text-sm font-bold flex-1 xl:flex-none justify-center"
                        >
                          <CheckCircle2 className="w-4 h-4" /> 搞定
                        </button>
                        <button
                          onClick={() => handleTaskAction(task.id, "failed")}
                          className="flex items-center gap-1 px-3 py-2 bg-stone-200 text-stone-600 rounded-lg hover:bg-stone-300 transition text-sm font-bold flex-1 xl:flex-none justify-center"
                        >
                          <XCircle className="w-4 h-4" /> 没完成
                        </button>
                        <button
                          onClick={() => handleTaskAction(task.id, "leave")}
                          className="flex items-center gap-1 px-3 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition text-sm font-bold w-full xl:w-auto justify-center mt-2 xl:mt-0"
                        >
                          <Coffee className="w-4 h-4" /> 请假卡
                        </button>
                      </div>
                    ) : (
                      <span
                        className={`font-bold px-4 py-1 rounded-full text-sm self-end xl:self-auto
                        ${
                          task.status === "done"
                            ? "bg-green-100 text-green-600"
                            : task.status === "failed"
                            ? "bg-stone-200 text-stone-500"
                            : "bg-blue-100 text-blue-600"
                        }`}
                      >
                        {task.status === "done"
                          ? "✅ 通关"
                          : task.status === "failed"
                          ? "❌ 未完成"
                          : "🛌 休息中"}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            )}

            {todayTasks.length > 0 &&
              todayTasks.every((t) => t.status !== "pending") && (
                <div className="mt-6 p-5 bg-gradient-to-r from-purple-50 to-pink-50 rounded-2xl border border-purple-100">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-bold text-purple-700 flex items-center gap-1">
                      <Wand2 className="w-5 h-5" /> 夸夸精灵的信
                    </h3>
                    <button
                      onClick={generatePraise}
                      disabled={isGeneratingPraise}
                      className="px-3 py-1.5 bg-purple-200 text-purple-700 rounded-lg text-sm font-bold hover:bg-purple-300 transition disabled:opacity-50"
                    >
                      {isGeneratingPraise
                        ? "精灵思考中..."
                        : aiPraise
                        ? "重新呼唤精灵"
                        : "召唤夸夸精灵"}
                    </button>
                  </div>
                  {aiPraise ? (
                    <p className="text-purple-800 font-medium leading-relaxed bg-white/60 p-3 rounded-xl">
                      {aiPraise}
                    </p>
                  ) : (
                    <p className="text-purple-400 text-sm">
                      点击召唤，听听精灵想对安安说什么~
                    </p>
                  )}
                </div>
              )}
          </div>

          <div className="bg-white rounded-3xl p-5 md:p-6 shadow-sm border border-stone-100">
            <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
              <Heart className="w-6 h-6 text-pink-400 fill-current" /> 当月战绩
            </h2>
            <div className="grid grid-cols-4 gap-2 mb-6 text-center">
              <div className="bg-green-50 p-2 rounded-xl border border-green-100">
                <div className="text-xl font-black text-green-600">
                  {stats.done}
                </div>
                <div className="text-xs text-green-700">通关</div>
              </div>
              <div className="bg-stone-50 p-2 rounded-xl border border-stone-200">
                <div className="text-xl font-black text-stone-600">
                  {stats.failed}
                </div>
                <div className="text-xs text-stone-500">未完成</div>
              </div>
              <div className="bg-blue-50 p-2 rounded-xl border border-blue-100">
                <div className="text-xl font-black text-blue-600">
                  {stats.leave}
                </div>
                <div className="text-xs text-blue-700">请假</div>
              </div>
              <div className="bg-pink-50 p-2 rounded-xl border border-pink-100">
                <div className="text-xl font-black text-pink-500">
                  {stats.leaveCards}
                </div>
                <div className="text-xs text-pink-600">剩余请假卡</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 后台管理设置面板 */}
      {showSettings && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-3xl w-full max-w-lg shadow-2xl overflow-hidden flex flex-col max-h-[85vh]">
            <div className="p-5 border-b border-stone-100 flex justify-between items-center bg-stone-50">
              <h2 className="text-lg font-bold flex items-center gap-2">
                <Settings className="w-5 h-5" /> 后台管理
              </h2>
              <button
                onClick={() => setShowSettings(false)}
                className="text-stone-400 hover:text-stone-600"
              >
                <XCircle className="w-6 h-6" />
              </button>
            </div>

            <div className="p-5 overflow-y-auto flex-1 space-y-6">
              {/* 语音聊天占比设置 */}
              <div className="bg-green-50 p-4 rounded-2xl border border-green-100">
                <h3 className="font-bold text-green-800 mb-2 flex items-center gap-1">
                  <MessageCircle className="w-4 h-4" /> 语音回复概率
                </h3>
                <p className="text-xs text-green-600 mb-3">
                  调节小猫在聊天中发语音消息的频率。当前：{voiceRatio}%
                </p>
                <div className="flex items-center gap-4">
                  <span className="text-xs text-stone-500">纯文字</span>
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={voiceRatio}
                    onChange={(e: any) =>
                      setVoiceRatio(parseInt(e.target.value))
                    }
                    className="flex-1 accent-green-500"
                  />
                  <span className="text-xs text-stone-500">全语音</span>
                </div>
              </div>

              <div className="bg-blue-50 p-4 rounded-2xl border border-blue-100">
                <h3 className="font-bold text-blue-800 mb-2 flex items-center gap-1">
                  <Wand2 className="w-4 h-4" /> AI 灵感任务池
                </h3>
                <p className="text-xs text-blue-600 mb-3">
                  不知道安排什么挑战？告诉 AI 主题，帮你一键生成！
                </p>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={aiInspirationTopic}
                    onChange={(e: any) => setAiInspirationTopic(e.target.value)}
                    placeholder="输入主题，如：自然探索、简单的家务..."
                    className="flex-1 px-3 py-2 text-sm bg-white border border-blue-200 rounded-xl outline-none focus:ring-2 focus:ring-blue-400"
                  />
                  <button
                    onClick={generateTaskInspiration}
                    disabled={isGeneratingTasks || !aiInspirationTopic.trim()}
                    className="bg-blue-500 text-white px-3 py-2 rounded-xl text-sm font-bold hover:bg-blue-600 transition disabled:opacity-50 whitespace-nowrap"
                  >
                    {isGeneratingTasks ? "施法中..." : "生成并添加"}
                  </button>
                </div>
              </div>

              <div>
                <h3 className="font-bold text-stone-700 mb-3 flex items-center gap-1">
                  <Edit3 className="w-4 h-4" /> 现有任务列表
                </h3>
                <div className="flex gap-2 mb-3">
                  <input
                    type="text"
                    value={newTaskInput}
                    onChange={(e: any) => setNewTaskInput(e.target.value)}
                    placeholder="手动输入新任务..."
                    className="flex-1 px-3 py-2 text-sm bg-stone-100 border-none rounded-xl focus:ring-2 focus:ring-pink-400 outline-none"
                    onKeyDown={(e: any) => {
                      if (e.key === "Enter" && newTaskInput.trim()) {
                        setTasks([...tasks, newTaskInput.trim()]);
                        setNewTaskInput("");
                      }
                    }}
                  />
                  <button
                    onClick={() => {
                      if (newTaskInput.trim()) {
                        setTasks([...tasks, newTaskInput.trim()]);
                        setNewTaskInput("");
                      }
                    }}
                    className="bg-stone-800 text-white px-4 py-2 rounded-xl hover:bg-stone-700 transition flex items-center gap-1"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
                <div className="space-y-2">
                  {tasks.map((task: any, idx: any) => (
                    <div
                      key={idx}
                      className="flex justify-between items-center p-2 bg-stone-50 rounded-lg group hover:bg-stone-100 text-sm"
                    >
                      <span className="text-stone-700 truncate mr-2">
                        {task}
                      </span>
                      <button
                        onClick={() =>
                          setTasks(tasks.filter((_, i) => i !== idx))
                        }
                        className="text-red-400 opacity-100 md:opacity-0 md:group-hover:opacity-100 hover:text-red-600 transition p-1"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 悬浮的聊天呼出按钮 */}
      <button
        onClick={() => setShowChat(true)}
        className="fixed bottom-6 right-6 p-4 bg-pink-500 text-white rounded-full shadow-xl hover:scale-110 transition-transform z-40"
      >
        <MessageCircle className="w-8 h-8" />
      </button>

      {/* 微信风格聊天弹窗 */}
      {showChat && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-end md:items-center justify-center p-0 md:p-4 z-50">
          <div className="bg-stone-50 md:rounded-3xl w-full h-[85vh] md:h-[600px] max-w-md shadow-2xl flex flex-col overflow-hidden rounded-t-3xl md:rounded-b-3xl">
            {/* 聊天框顶部标题栏 */}
            <div className="bg-pink-500 p-4 text-white flex justify-between items-center shrink-0">
              <span className="font-bold flex items-center gap-2">
                <Sparkles className="w-5 h-5" /> 魔法哈基米聊天室
              </span>
              <button
                onClick={() => setShowChat(false)}
                className="hover:text-pink-200"
              >
                <XCircle className="w-6 h-6" />
              </button>
            </div>

            {/* 聊天消息显示区 */}
            <div
              ref={chatContainerRef}
              className="flex-1 overflow-y-auto p-4 space-y-4"
            >
              {messages.map((msg: any, idx: any) => (
                <div
                  key={idx}
                  className={`flex ${
                    msg.role === "user" ? "justify-end" : "justify-start"
                  }`}
                >
                  {msg.role === "user" ? (
                    <div className="max-w-[75%] p-3 rounded-2xl bg-pink-400 text-white rounded-tr-sm shadow-sm">
                      {msg.text}
                    </div>
                  ) : msg.type === "loading_audio" ? (
                    <div className="bg-white text-stone-400 shadow-sm rounded-tl-sm border border-stone-100 p-3 rounded-2xl text-sm flex items-center gap-2">
                      <span className="animate-pulse">🎤</span>{" "}
                      哈基米正在录音喵...
                    </div>
                  ) : msg.type === "audio" ? (
                    <div className="flex flex-col gap-1 items-start max-w-[75%]">
                      <div
                        onClick={() => playAudio(msg.audioUrl, idx)}
                        className="bg-white text-stone-700 shadow-sm rounded-tl-sm border border-stone-100 p-3 rounded-2xl flex items-center gap-3 cursor-pointer w-40 hover:bg-stone-50 active:bg-stone-100 transition"
                      >
                        <span className="text-green-500">
                          {playingMsgIndex === idx ? "🔊" : "🔈"}
                        </span>
                        <span className="text-sm font-medium text-stone-500">
                          {playingMsgIndex === idx ? "播放中..." : "语音消息"}
                        </span>
                      </div>
                      {/* 语音条下方显示文字翻译，方便小朋友看 */}
                      <span className="text-xs text-stone-400 pl-1">
                        {msg.text}
                      </span>
                    </div>
                  ) : (
                    <div className="max-w-[75%] p-3 rounded-2xl bg-white text-stone-700 shadow-sm rounded-tl-sm border border-stone-100">
                      {msg.text}
                    </div>
                  )}
                </div>
              ))}

              {/* 打字动画提示 */}
              {isChatting && (
                <div className="flex justify-start">
                  <div className="bg-white p-3 rounded-2xl rounded-tl-sm shadow-sm text-stone-400 text-sm">
                    哈基米正在敲键盘...
                  </div>
                </div>
              )}
            </div>

            {/* 底部输入框区 */}
            <div className="bg-white p-3 border-t border-stone-200 flex items-center gap-2 shrink-0">
              <input
                type="text"
                value={inputText}
                onChange={(e: any) => setInputText(e.target.value)}
                onKeyDown={(e: any) => e.key === "Enter" && handleSendMessage()}
                placeholder="跟哈基米说点什么喵..."
                className="flex-1 bg-stone-100 px-4 py-2 rounded-full outline-none focus:ring-2 focus:ring-pink-300"
              />
              <button
                onClick={handleSendMessage}
                disabled={!inputText.trim() || isChatting}
                className="bg-pink-500 text-white px-4 py-2 rounded-full font-bold hover:bg-pink-600 disabled:opacity-50 transition-colors"
              >
                发送
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
